import { useCallback, useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'motion/react';
import { usePlaybackGate } from './PlaybackGate';

/*
 * The playback contract every product vignette runs on.
 *
 * A vignette is a story told in steps: step 0 is how it opens, step N (the
 * timeline's length) is how it ends, and the vignette renders whatever `step`
 * says. This hook only decides WHEN the step moves:
 *
 * - it starts once the vignette is at least `threshold` in view (an
 *   IntersectionObserver, never a scroll listener),
 * - it pauses where it is when the vignette leaves view (drops under
 *   `pauseThreshold`, the start threshold unless given), and resumes with
 *   what was left of that step's hold when it comes back,
 * - with `loop` it holds the last step for `rest` ms, then starts over (the
 *   new pass bumps `cycle`, so a vignette can key a reset transition on it);
 *   with `replayOnEnter` a finished run starts over the next time the
 *   vignette comes back from fully off-screen,
 * - under reduced motion it reports the last step from the first render and
 *   never ticks: the vignette shows its end state, still,
 * - inside a closed PlaybackGate it holds on step 0 and does not start; a
 *   pass under way when the gate closes runs to its end (with `loop`, back
 *   to step 0) and holds there.
 *
 * Step 0 is on screen from the first paint, so a vignette's opening state
 * must be complete in itself (a hero never starts blank).
 */

// A vignette taller than the viewport can never show `threshold` of itself;
// it counts as in view once it fills that share of the viewport instead. The
// intermediate thresholds let the observer report that as it scrolls in.
function thresholdsFor(threshold) {
    return [0, threshold / 4, threshold / 2, (threshold * 3) / 4, threshold];
}

function isInView(entry, threshold) {
    if (!entry.isIntersecting) return false;
    // Observers report ratios that land a hair under the threshold that fired them.
    if (entry.intersectionRatio >= threshold - 0.005) return true;
    const viewport = entry.rootBounds?.height;

    return Boolean(viewport) && entry.intersectionRect.height >= viewport * threshold;
}

/**
 * @param {object} options
 * @param {number[]} options.timeline How long (ms) each step holds before the
 *   next: `timeline[0]` is the pause before anything moves, and the steps run
 *   from 0 to `timeline.length`, the final one.
 * @param {boolean} [options.loop] Start over after the final step.
 * @param {number} [options.rest] With `loop`: how long (ms) the final step holds.
 * @param {boolean} [options.replayOnEnter] Without `loop`: start over when the
 *   vignette re-enters after having been fully off-screen.
 * @param {number} [options.threshold] Share of the vignette (0 to 1) that must
 *   be visible for it to play. 0.35 by default.
 * @param {number} [options.pauseThreshold] Share under which a playing
 *   vignette pauses; 0 keeps it playing until it is entirely off screen. The
 *   start threshold by default.
 * @param {boolean|function(number): boolean} [options.enabled] False holds
 *   the current step (a vignette still loading a part of itself, say); a
 *   function is asked with the step about to be held (a story that holds
 *   only before the beats that need a part). True by default.
 * @returns {{ ref: Function, step: number, finalStep: number, playing: boolean, reduced: boolean, inView: boolean, cycle: number }}
 *   `ref` goes on the vignette's outermost element.
 */
export default function usePlayback({
    timeline = [],
    loop = false,
    rest = 0,
    replayOnEnter = false,
    threshold = 0.35,
    pauseThreshold = threshold,
    enabled = true,
} = {}) {
    const reduced = Boolean(useReducedMotion());
    const finalStep = timeline.length;
    const [node, setNode] = useState(null);
    const [inView, setInView] = useState(false);
    const [step, setStep] = useState(0);
    const [cycle, setCycle] = useState(0);

    // The latest timing, read when a hold is scheduled: callers pass inline
    // arrays, and a new array each render must not restart the current hold.
    const timing = useRef({ timeline, rest, loop, replayOnEnter });
    timing.current = { timeline, rest, loop, replayOnEnter };

    // What was left of an interrupted hold, and which hold it belongs to.
    const paused = useRef(null);
    // The step as last rendered, for the observer's callback.
    const currentStep = useRef(step);
    currentStep.current = step;

    const finished = !loop && step >= finalStep;
    const gateOpen = usePlaybackGate();
    const allowed = (typeof enabled === 'function' ? Boolean(enabled(step)) : enabled) && (gateOpen || step !== 0);
    const playing = allowed && !reduced && inView && !finished;

    useEffect(() => {
        if (reduced || !node) return undefined;
        if (typeof IntersectionObserver === 'undefined') {
            // Nothing can report visibility: play, rather than never.
            setInView(true);
            return undefined;
        }

        const observer = new IntersectionObserver((entries) => {
            const entry = entries[entries.length - 1];
            // Starting needs `threshold`; once playing, it takes dropping
            // under `pauseThreshold` to stop.
            setInView((wasInView) => isInView(entry, wasInView ? Math.min(pauseThreshold, threshold) : threshold));

            // Fully gone after a finished run: rewind out of sight, so the
            // next visit plays it again.
            const { replayOnEnter: replay, loop: loops, timeline: holds } = timing.current;
            if (!entry.isIntersecting && replay && !loops && currentStep.current >= holds.length) {
                paused.current = null;
                currentStep.current = 0;
                setStep(0);
                setCycle((count) => count + 1);
            }
        }, { threshold: thresholdsFor(threshold) });
        observer.observe(node);

        return () => observer.disconnect();
    }, [node, reduced, threshold, pauseThreshold]);

    useEffect(() => {
        if (!playing) return undefined;

        const { timeline: holds, rest: finalHold } = timing.current;
        const full = step >= holds.length ? finalHold : (holds[step] ?? 0);
        const resumed = paused.current && paused.current.step === step && paused.current.cycle === cycle;
        const wait = Math.max(0, resumed ? paused.current.remaining : full);
        paused.current = null;
        const startedAt = Date.now();
        let fired = false;

        const timer = setTimeout(() => {
            fired = true;
            if (step >= timing.current.timeline.length) {
                setStep(0);
                setCycle((count) => count + 1);
            } else {
                setStep(step + 1);
            }
        }, wait);

        return () => {
            clearTimeout(timer);
            if (!fired) {
                paused.current = { step, cycle, remaining: Math.max(0, wait - (Date.now() - startedAt)) };
            }
        };
    }, [playing, step, cycle]);

    const ref = useCallback((element) => setNode(element), []);

    return {
        ref,
        step: reduced ? finalStep : Math.min(step, finalStep),
        finalStep,
        playing,
        reduced,
        inView,
        cycle,
    };
}
